import React, { useState } from "react";
import { DivisionLeague, LeagueMatch } from "../types/league";
import LeagueMatchComponent from "./LeagueMatch";
import {
  Users,
  Calendar,
  Target,
  TrendingUp,
  Clock,
  ArrowLeft,
  CheckCircle,
  XCircle,
} from "lucide-react";

interface DivisionLeagueDashboardProps {
  league: DivisionLeague;
  setLeague: (league: DivisionLeague) => void;
  onReset: () => void;
}

const DivisionLeagueDashboard: React.FC<DivisionLeagueDashboardProps> = ({
  league,
  setLeague,
  onReset,
}) => {
  const [activeView, setActiveView] = useState<
    "divisions" | "schedule" | "stats" | "matches"
  >("divisions");
  const [selectedMatch, setSelectedMatch] = useState<LeagueMatch | null>(null);

  const completedMatches = league.weeks.flatMap((week) =>
    week.matches.filter((match) => match.isCompleted)
  );
  const pendingMatches = league.weeks.flatMap((week) =>
    week.matches.filter((match) => !match.isCompleted)
  );

  const handleMatchClick = (match: LeagueMatch | any) => {
    if (!match.isCompleted) {
      setSelectedMatch(match);
    }
  };

  const handleEnterScore = (
    matchId: string,
    team1Score: number,
    team2Score: number
  ) => {
    // Find and update the match
    const updatedWeeks = league.weeks.map((week) => ({
      ...week,
      matches: week.matches.map((match) => {
        if (match.id === matchId) {
          return {
            ...match,
            result: {
              team1Score,
              team2Score,
              winner: team1Score > team2Score ? match.team1.id : match.team2.id,
              loser: team1Score > team2Score ? match.team2.id : match.team1.id,
              completed: true,
              isPerfectGame:
                (team1Score === 11 && team2Score === 0) ||
                (team2Score === 11 && team1Score === 0),
              isCloseMatch: Math.abs(team1Score - team2Score) <= 2,
            },
            isCompleted: true,
          };
        }
        return match;
      }),
    }));

    // Update division standings
    const updatedDivisions = league.divisions.map((division) => {
      const divisionMatches = updatedWeeks.flatMap((week) =>
        week.matches.filter(
          (match) =>
            match.isCompleted &&
            (division.teams.some((team) => team.id === match.team1.id) ||
              division.teams.some((team) => team.id === match.team2.id))
        )
      );

      const updatedStandings = division.teams.map((team) => {
        const teamMatches = divisionMatches.filter(
          (match) => match.team1.id === team.id || match.team2.id === team.id
        );

        let wins = 0;
        let losses = 0;
        let points = 0;
        let gamesPlayed = 0;
        let gamesWon = 0;
        let gamesLost = 0;

        teamMatches.forEach((match) => {
          if (match.result) {
            gamesPlayed++;
            if (match.team1.id === team.id) {
              gamesWon += match.result.team1Score;
              gamesLost += match.result.team2Score;
              if (match.result.winner === team.id) {
                wins++;
                points += 3; // Win = 3 points
              } else {
                losses++;
                if (match.result.isCloseMatch) {
                  points += 1; // Close loss = 1 point
                }
              }
            } else {
              gamesWon += match.result.team2Score;
              gamesLost += match.result.team1Score;
              if (match.result.winner === team.id) {
                wins++;
                points += 3;
              } else {
                losses++;
                if (match.result.isCloseMatch) {
                  points += 1;
                }
              }
            }
          }
        });

        return {
          team,
          wins,
          losses,
          points,
          gamesPlayed,
          gamesWon,
          gamesLost,
          winPercentage: gamesPlayed > 0 ? (wins / gamesPlayed) * 100 : 0,
          pointsPerGame: gamesPlayed > 0 ? points / gamesPlayed : 0,
          streak: 0,
          lastResult: null,
        };
      });

      // Sort standings by points, then win percentage
      updatedStandings.sort((a, b) => {
        if (b.points !== a.points) return b.points - a.points;
        return b.winPercentage - a.winPercentage;
      });

      return {
        ...division,
        standings: updatedStandings,
      };
    });

    const updatedLeague = {
      ...league,
      weeks: updatedWeeks,
      divisions: updatedDivisions,
      stats: {
        ...league.stats,
        totalMatches: updatedWeeks.flatMap((week) => week.matches).length,
        completedMatches: updatedWeeks
          .flatMap((week) => week.matches)
          .filter((match) => match.isCompleted).length,
      },
      updatedAt: new Date(),
    };

    setLeague(updatedLeague);
    setSelectedMatch(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={onReset}
                className="flex items-center text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Home
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {league.name}
                </h1>
                <p className="text-gray-600">Division League</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm text-gray-600">Week</p>
                <p className="font-semibold">
                  {league.currentWeek} of {league.totalWeeks}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {[
              { id: "divisions", name: "Divisions", icon: Target },
              { id: "matches", name: "Matches", icon: Calendar },
              { id: "schedule", name: "Schedule", icon: Clock },
              { id: "stats", name: "Statistics", icon: TrendingUp },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveView(item.id as any)}
                  className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                    activeView === item.id
                      ? "border-green-500 text-green-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {item.name}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Divisions View */}
        {activeView === "divisions" && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {league.divisions.map((division) => (
                <div key={division.id} className="bg-white rounded-lg shadow">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {division.name}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {division.teams.length} teams
                    </p>
                  </div>
                  <div className="p-6">
                    <div className="space-y-3">
                      {division.standings.slice(0, 5).map((standing, index) => (
                        <div
                          key={standing.team.id}
                          className="flex items-center justify-between"
                        >
                          <div className="flex items-center space-x-3">
                            <span className="text-sm font-medium text-gray-900">
                              {index + 1}.
                            </span>
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {standing.team.name}
                              </div>
                              <div className="text-xs text-gray-500">
                                {standing.team.player1} &{" "}
                                {standing.team.player2}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-medium text-gray-900">
                              {standing.points} pts
                            </div>
                            <div className="text-xs text-gray-500">
                              {standing.wins}-{standing.losses}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Matches View */}
        {activeView === "matches" && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">
                  All Matches
                </h2>
                <p className="text-sm text-gray-600">
                  {pendingMatches.length} pending, {completedMatches.length}{" "}
                  completed
                </p>
              </div>
              <div className="divide-y divide-gray-200">
                {league.weeks
                  .flatMap((week) => week.matches)
                  .map((match) => (
                    <div key={match.id} className="px-6 py-4">
                      <LeagueMatchComponent
                        match={match}
                        onClick={handleMatchClick}
                        variant="compact"
                        showWeek={true}
                      />
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}

        {/* Schedule View */}
        {activeView === "schedule" && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">
                  League Schedule
                </h2>
                <p className="text-sm text-gray-600">
                  Week {league.currentWeek} of {league.totalWeeks}
                </p>
              </div>
              <div className="p-6">
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p className="text-lg font-medium mb-2">Division Schedule</p>
                  <p className="text-sm">
                    Intra-division and inter-division matches will be scheduled
                    based on league settings.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Statistics View */}
        {activeView === "stats" && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Target className="w-8 h-8 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">
                      Divisions
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {league.divisions.length}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Users className="w-8 h-8 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">
                      Total Teams
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {league.divisions.reduce(
                        (total, div) => total + div.teams.length,
                        0
                      )}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <TrendingUp className="w-8 h-8 text-purple-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">
                      Total Matches
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {league.stats.totalMatches}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  League Settings
                </h3>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">
                      Division Structure
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">
                          Number of Divisions:
                        </span>
                        <span className="font-medium">
                          {league.settings.numberOfDivisions}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">
                          Teams per Division:
                        </span>
                        <span className="font-medium">
                          {league.settings.teamsPerDivision}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">
                          Intra-Division Weeks:
                        </span>
                        <span className="font-medium">
                          {league.settings.intraDivisionWeeks}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">
                          Inter-Division Weeks:
                        </span>
                        <span className="font-medium">
                          {league.settings.interDivisionWeeks}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">
                      Season Information
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Current Week:</span>
                        <span className="font-medium">
                          {league.currentWeek} of {league.totalWeeks}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Playoff Weeks:</span>
                        <span className="font-medium">
                          {league.settings.playoffWeeks}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Season Start:</span>
                        <span className="font-medium">
                          {new Date(
                            league.settings.seasonStartDate
                          ).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Season End:</span>
                        <span className="font-medium">
                          {new Date(
                            league.settings.seasonEndDate
                          ).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Score Entry Modal */}
      {selectedMatch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Enter Match Score
              </h3>
              <button
                onClick={() => setSelectedMatch(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {selectedMatch.team1.name} Score
                </label>
                <input
                  type="number"
                  min="0"
                  max="13"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="0-13"
                  id="team1Score"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {selectedMatch.team2.name} Score
                </label>
                <input
                  type="number"
                  min="0"
                  max="13"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="0-13"
                  id="team2Score"
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                <div className="text-sm text-blue-800">
                  <strong>Pickleball Rules:</strong>
                  <ul className="mt-1 space-y-1">
                    <li>• First to 11 points wins</li>
                    <li>• Must win by 2 points</li>
                    <li>• Maximum score is 13 points</li>
                  </ul>
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => setSelectedMatch(null)}
                  className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const team1Score = parseInt(
                      (
                        document.getElementById(
                          "team1Score"
                        ) as HTMLInputElement
                      ).value
                    );
                    const team2Score = parseInt(
                      (
                        document.getElementById(
                          "team2Score"
                        ) as HTMLInputElement
                      ).value
                    );
                    if (
                      !isNaN(team1Score) &&
                      !isNaN(team2Score) &&
                      team1Score >= 0 &&
                      team1Score <= 13 &&
                      team2Score >= 0 &&
                      team2Score <= 13 &&
                      team1Score !== team2Score
                    ) {
                      handleEnterScore(
                        selectedMatch.id,
                        team1Score,
                        team2Score
                      );
                    }
                  }}
                  className="flex-1 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
                >
                  Submit Score
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DivisionLeagueDashboard;
